import {
	BigNum,
	BN,
	ContractTier,
	oraclePriceBands as getOraclePriceBands,
	PositionDirection,
} from '@velocity-exchange/sdk';
import {
	MarketId,
	getPerpAuctionDuration,
	getLimitAuctionParams,
	getDefaultLimitAuctionDurationSlots,
} from '@velocity-exchange/common';
import { SlotDurationMs, VelocityClient } from '@velocity-exchange/sdk';
import { syncGetCurrentSettings } from 'src/environmentVariables/EnvironmentVariables';
import { TRADE_PREP_UTILS } from 'src/stores/VelocityStore/actions/actionHelpers/tradePrep';
interface Params {
	velocityClient: VelocityClient;
	direction: PositionDirection;
	marketId: MarketId;
	inputPrice: BigNum;
	auctionStartPrice: BN;
	oraclePrice: BN;
	/** Live slot duration; the perp ramp is a program mirror and must match it. */
	slotDuration: SlotDurationMs;
}

/**
 * Performs some common upfront work for calling `getLimitAuctionParams`,
 * calls it, and returns the result.
 */
export function buildLimitAuctionParams({
	velocityClient,
	direction,
	marketId,
	inputPrice,
	auctionStartPrice,
	oraclePrice,
	slotDuration,
}: Params) {
	const settings = syncGetCurrentSettings();

	const perpMarketAccount = marketId.isPerp
		? velocityClient.getPerpMarketAccount(marketId.marketIndex)
		: undefined;

	const oraclePriceBands = (() => {
		if (!perpMarketAccount || !marketId.isPerp) {
			return undefined;
		}

		const marketOracleData = velocityClient.getOracleDataForPerpMarket(
			marketId.marketIndex
		);

		if (!marketOracleData?.price) {
			return undefined;
		}

		return getOraclePriceBands(perpMarketAccount, marketOracleData);
	})();

	const auctionDuration = marketId.isPerp
		? getPerpAuctionDuration(
				inputPrice.val.sub(auctionStartPrice).abs(),
				oraclePrice.abs(),
				perpMarketAccount?.contractTier ?? ContractTier.B,
				slotDuration
			)
		: getDefaultLimitAuctionDurationSlots(slotDuration);

	return getLimitAuctionParams({
		direction,
		inputPrice,
		startPriceFromSettings: auctionStartPrice, // Use the passed-in auctionStartPrice directly
		duration: auctionDuration,
		auctionStartPriceOffset:
			TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffset(
				settings?.auctionStartPriceOffset as number | 'marketBased',
				marketId
			),
		oraclePriceBands,
	});
}
